import { Component, HostBinding, input, Input } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { environment } from '@core/environments/environment';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'mat-error',
  styleUrls: ['./mat-error.component.scss'],
  template: `
    @if ((control || customErrorMessage()) && errorMessage !== null) {
      <ng-container>
        {{ errorMessage }}
        <ng-content />
      </ng-container>
    }
  `,
})
export class MatErrorComponent {
  public readonly fieldName = input<string>('');
  public readonly customErrorMessage = input<string>('');
  public readonly positionAbsolute = input<boolean>(false);

  @HostBinding('class.mat-error--position-absolute')
  public get positionAbsoluteClass(): boolean {
    return this.positionAbsolute();
  }

  private readonly customErrorPrefix = 'shared.form.customErrors' as const;
  private readonly errorPrefix = 'shared.form.error' as const;
  private abstractControl: AbstractControl | null = null;

  @Input()
  public set control(value: AbstractControl | null) {
    this.abstractControl = value;
  }

  public get control(): AbstractControl | null {
    return this.abstractControl;
  }

  public get errorMessage(): string | null {
    if (this.control && this.control.errors && !this.customErrorMessage()) {
      for (const errorName in this.control.errors) {
        if (this.control.errors[errorName]) {
          return this.getErrorMessage(errorName, this.control.errors[errorName]);
        }
      }
    }

    return this.customErrorMessage() || null;
  }

  private readonly staticErrors: Record<string, string>;
  private readonly errors = {
    allFieldsRequired: () => this.staticErrors['allFieldsRequired'],
    dateRange: () => this.translate.instant('shared.form.error.dateRange'),
    durationComparison: () => this.translate.instant('shared.form.error.durationComparison'),
    email: () => this.staticErrors['email'],
    gameNameNotExists: () => this.staticErrors['gameNameNotExists'],
    invalidExtension: () => this.translate.instant('shared.form.error.invalidExtension'),
    itemCategoryDuplicated: () => this.staticErrors['itemCategoryDuplicated'],
    matDatepickerMin: () => this.staticErrors['matDatepickerMin'],
    matDatepickerParse: () => this.translate.instant('shared.form.error.matDatepickerParse'),
    matTimepickerMax: () => this.translate.instant('shared.form.error.matTimepickerMax'),
    matTimepickerMin: () => this.staticErrors['matTimepickerMin'],
    matTimepickerParse: () => this.translate.instant('shared.form.error.matTimepickerParse'),
    maxlength: ({ requiredLength }: { requiredLength: number }) =>
      this.translate.instant('shared.form.error.maxLength', { requiredLength }),
    min: ({ min }: { min: number }) => this.translate.instant('shared.form.error.min', { min }),
    minlength: ({ requiredLength }: { requiredLength: number }) =>
      this.translate.instant('shared.form.error.minLength', { requiredLength }),
    mustBeDecimal: () => this.staticErrors['mustBeDecimal'],
    mustBeNumber: () => this.staticErrors['mustBeNumber'],
    passwordsDoNotMatch: () => this.staticErrors['passwordsDoNotMatch'],
    required: () => this.staticErrors['required'],
  } as const;

  constructor(private readonly translate: TranslateService) {
    this.staticErrors = {
      allFieldsRequired: this.translate.instant('shared.form.error.allFieldsRequired'),
      dateRange: this.translate.instant('shared.form.error.dateRange'),
      durationComparison: this.translate.instant('shared.form.error.durationComparison'),
      email: this.translate.instant('shared.form.error.email'),
      gameNameNotExists: this.translate.instant('shared.form.error.gameNameNotExists'),
      invalidExtension: this.translate.instant('shared.form.error.invalidExtension'),
      matDatepickerMin: this.translate.instant('shared.form.error.matDatepickerMin'),
      matDatepickerParse: this.translate.instant('shared.form.error.matDatepickerParse'),
      matTimepickerMax: this.translate.instant('shared.form.error.matTimepickerMax'),
      matTimepickerMin: this.translate.instant('shared.form.error.matTimepickerMin'),
      matTimepickerParse: this.translate.instant('shared.form.error.matTimepickerParse'),
      mustBeDecimal: this.translate.instant('shared.form.error.mustBeDecimal'),
      mustBeNumber: this.translate.instant('shared.form.error.mustBeNumber'),
      passwordsDoNotMatch: this.translate.instant('shared.form.error.passwordsDoNotMatch'),
      required: this.translate.instant('shared.form.error.required'),
      unknown: this.translate.instant('shared.form.error.unknown'),
    } as const;
  }

  private getErrorMessage(errorName: string, errorValue: unknown): string | null {
    const customErrorMessage = this.getCustomErrorMessage(errorName, errorValue);

    if (customErrorMessage) {
      return customErrorMessage;
    }

    return errorName in this.errors
      ? this.errors[errorName as keyof MatErrorComponent['errors']](
          errorValue as { requiredLength: number } & { min: number },
        )
      : this.staticErrors['unknown'];
  }

  private getCustomErrorMessage(errorName: string, errorValue: unknown): string | null {
    const translationKey = `${this.fieldName() ? this.customErrorPrefix : this.errorPrefix}.${errorName}${this.fieldName() ? `.${this.fieldName()}` : ''}`;
    const translation = this.translate.instant(translationKey, {
      [errorName]: (errorValue as Record<string, unknown>)?.[errorName],
      ...(errorValue instanceof Object ? errorValue : {}),
    });

    if (translation !== translationKey) {
      return translation;
    }

    if (!environment.production) {
      // eslint-disable-next-line no-console
      console.error(`Missing translation for ${translationKey}`);
    }

    return null;
  }
}
